import { type Nullable } from "core/types";
import { type IDisposable, type Scene } from "core/scene";
import { type IService, type ServiceDefinition } from "shared-ui-components/modularTool/modularity/serviceDefinition";
import { type IShellService, ShellServiceIdentity } from "shared-ui-components/modularTool/services/shellService";
import { type DynamicAccordionSection, type DynamicAccordionSectionContent } from "shared-ui-components/modularTool/components/extensibleAccordion";
import { WrenchRegular } from "@fluentui/react-icons";
import { useObservableCollection, useObservableState, useOrderedObservableCollection } from "shared-ui-components/modularTool/hooks/observableHooks";
import { ObservableCollection } from "shared-ui-components/modularTool/misc/observableCollection";
import { ToolsPane } from "../../components/tools/toolsPane";
import { SceneContextIdentity, type ISceneContext } from "../sceneContext";

/**
 * The unique identity symbol for the tools service.
 */
export const ToolsServiceIdentity = Symbol("ToolsService");

/**
 * A service that provides tools for the user to generate artifacts or perform actions on entities.
 */
export interface IToolsService extends IService<typeof ToolsServiceIdentity> {
    /**
     * Adds a new section (e.g. "Export", "Capture", etc.).
     * @param section A description of the section to add.
     */
    addSection(section: DynamicAccordionSection): IDisposable;

    /**
     * Adds content to one or more sections.
     * @param content A description of the content to add.
     */
    addSectionContent(content: DynamicAccordionSectionContent<Scene>): IDisposable;
}

/**
 * A collection of usually optional, dynamic extensions.
 * Common examples includes importing/exporting, or other general creation tools.
 */
export const ToolsServiceDefinition: ServiceDefinition<[IToolsService], [IShellService, ISceneContext]> = {
    friendlyName: "Tools Editor",
    produces: [ToolsServiceIdentity],
    consumes: [ShellServiceIdentity, SceneContextIdentity],
    factory: (shellService, sceneContext) => {
        const sectionsCollection = new ObservableCollection<DynamicAccordionSection>();
        const sectionContentCollection = new ObservableCollection<DynamicAccordionSectionContent<Scene>>();

        // Only show the Tools pane if some tool content has been added.
        let toolsPaneRegistration: Nullable<IDisposable> = null;
        sectionContentCollection.observable.add(() => {
            if (sectionContentCollection.items.length === 0) {
                toolsPaneRegistration?.dispose();
                toolsPaneRegistration = null;
            } else if (!toolsPaneRegistration) {
                toolsPaneRegistration = shellService.addSidePane({
                    key: "Tools",
                    title: "Tools",
                    icon: WrenchRegular,
                    horizontalLocation: "right",
                    verticalLocation: "top",
                    order: 400,
                    teachingMoment: false,
                    content: () => {
                        const sections = useOrderedObservableCollection(sectionsCollection);
                        const sectionContent = useObservableCollection(sectionContentCollection);
                        const scene = useObservableState(() => sceneContext.currentScene, sceneContext.currentSceneObservable);

                        return (
                            scene && (
                                <ToolsPane
                                    uniqueId="Tools"
                                    sections={sections}
                                    sectionContent={sectionContent}
                                    context={scene}
                                    enablePinnedItems
                                    enableHiddenItems
                                    enableSearchItems
                                />
                            )
                        );
                    },
                });
            }
        });

        return {
            addSection: (section) => sectionsCollection.add(section),
            addSectionContent: (content) => sectionContentCollection.add(content),
            dispose: () => toolsPaneRegistration?.dispose(),
        };
    },
};
